/**
 * @file FormulationMappingSettings.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-18
 * @modified 2026-09-18
 * @description Product mapping: the products that report another product's
 *              formulation because they have none of their own.
 *
 *              A mapping is only ever consulted while the product has no
 *              formulation, so one added later takes over on its own. A row
 *              marked "no longer needed" is therefore ignored rather than
 *              wrong, and can be tidied away whenever — or left.
 */

import { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config';
import './SearchLayout.css';
import './InvoiceDetailReport.css';
import './MapOfAgReport.css';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import RowActionsMenu from './RowActionsMenu';
import TableContextMenu from './TableContextMenu';
import useTableContextMenu from '../hooks/useTableContextMenu';
import FormulationSourceModal from './FormulationSourceModal';
import LoadingSpinner from './LoadingSpinner';
import { useSlideOutButton } from './SlideOutButton';

function FormulationMappingSettings() {
  const [substitutes, setSubstitutes] = useState([]);
  const [formulatedItems, setFormulatedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sourceFor, setSourceFor] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [alert, setAlert] = useState({ isOpen: false, message: '', type: 'info' });

  const say = (message, type = 'info') => setAlert({ isOpen: true, message, type });
  useSlideOutButton([substitutes.length, saving]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [subs, items] = await Promise.all([
        fetch(`${API_URL}/api/map-of-ag/formulation-substitutes`, { credentials: 'include' }).then((r) => r.json()),
        fetch(`${API_URL}/api/map-of-ag/formulated-items`, { credentials: 'include' }).then((r) => r.json())
      ]);
      setSubstitutes(subs.substitutes || []);
      setFormulatedItems(items.items || []);
    } catch (e) {
      say(`Could not load the mappings: ${e.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const ctxMenu = useTableContextMenu({ rows: substitutes, getRowId: (r) => r.inventory_item_id });

  const call = async (url, options) => {
    setSaving(true);
    try {
      const res = await fetch(url, { credentials: 'include', ...options });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'That did not work');
      return data;
    } catch (e) {
      say(e.message, 'error');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const changeSource = async (sub, sourceItemId) => {
    if (!sourceItemId || Number(sourceItemId) === sub.source_inventory_item_id) return;
    const done = await call(`${API_URL}/api/map-of-ag/formulation-substitutes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        itemId: sub.inventory_item_id,
        itemCode: sub.item_code,
        sourceItemId: Number(sourceItemId),
        sourceItemCode: formulatedItems.find((i) => i.id === Number(sourceItemId))?.item_code
      })
    });
    if (done) { await load(); say(`${sub.item_code} updated.`, 'success'); }
  };

  const remove = (sub) => {
    setConfirm({
      title: 'Remove this mapping?',
      message: sub.still_needed
        ? `${sub.item_code} has no formulation of its own, so it will report no soya or protein figures at all until one is added.`
        : `${sub.item_code} has its own formulation now, so this mapping is already being ignored.`,
      confirmText: 'Remove',
      type: 'warning',
      onConfirm: async () => {
        setConfirm(null);
        const done = await call(`${API_URL}/api/map-of-ag/formulation-substitutes/${sub.inventory_item_id}`,
          { method: 'DELETE' });
        if (done) { await load(); say('Removed.', 'success'); }
      }
    });
  };

  const buildRowActions = (sub) => [
    {
      key: 'change',
      icon: 'swap_horiz',
      label: 'Change mapping',
      title: "Choose a different product's formulation",
      onClick: () => setSourceFor(sub)
    },
    {
      key: 'remove',
      icon: 'delete',
      label: 'Remove mapping',
      title: 'Stop borrowing a formulation',
      onClick: () => remove(sub)
    }
  ];

  return (
    <div className="map-of-ag-report invoice-detail-report processor-settings">
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">Product mapping</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span style={{ margin: 'auto 0' }}>
              Products with no formulation of their own, reporting another product's analysis.
              A formulation added to the product itself always wins, so a mapping stops being
              used on its own. Right-click a row, or use the menu at the end of it.
            </span>
          </span>
        </div>
      </div>

      {loading ? <LoadingSpinner /> : (
        <div className="table-container">
          <table className="data-table substitutes-table" onContextMenu={ctxMenu.onTableContextMenu}>
            <thead>
              <tr>
                <th>Product</th>
                <th>Mapped to</th>
                <th>Status</th>
                <th className="actions-header no-sort" />
              </tr>
            </thead>
            <tbody>
              {substitutes.map((sub) => (
                <tr key={sub.inventory_item_id} className={`line-row${sub.still_needed ? '' : ' is-inactive'}`}>
                  <td>
                    <span className="sub-product">
                      <span className="account-badge-inline">{sub.item_code}</span>
                      <span className="sub-name">{sub.item_name}</span>
                    </span>
                  </td>
                  <td>
                    <button type="button" className="sub-source" onClick={() => setSourceFor(sub)}>
                      <span className="account-badge-inline">{sub.source_item_code}</span>
                      <span className="sub-name">{sub.source_item_name}</span>
                    </button>
                  </td>
                  <td>
                    {sub.still_needed
                      ? <span className="sub-state is-live">In use</span>
                      : <span className="sub-state">No longer needed — it has its own now</span>}
                  </td>
                  <td>
                    <div className="actions-cell">
                      <RowActionsMenu actions={buildRowActions(sub)} />
                    </div>
                  </td>
                </tr>
              ))}
              {!substitutes.length && (
                <tr><td colSpan={4} className="report-empty">No products are mapped to another's formulation.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <TableContextMenu
        ctx={ctxMenu}
        rowActions={buildRowActions}
        actions={[{ key: 'refresh', readSafe: true, icon: 'refresh', label: 'Refresh', onClick: load }]}
      />

      <FormulationSourceModal
        isOpen={!!sourceFor}
        forItem={sourceFor ? { item_code: sourceFor.item_code, item_name: sourceFor.item_name } : null}
        items={formulatedItems}
        currentSourceId={sourceFor?.source_inventory_item_id || null}
        onPick={(sourceId) => { const sub = sourceFor; setSourceFor(null); changeSource(sub, sourceId); }}
        onClose={() => setSourceFor(null)}
      />

      {confirm && <ConfirmDialog isOpen {...confirm} onCancel={() => setConfirm(null)} />}
      <AlertDialog
        isOpen={alert.isOpen}
        message={alert.message}
        type={alert.type}
        onClose={() => setAlert({ ...alert, isOpen: false })}
      />
    </div>
  );
}

export default FormulationMappingSettings;
